import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button, Typography } from '@mui/material';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { ServicePlan } from '../../../data/models/client-program.model';
import { PricingModel } from '../../../data/models/pricing-model.model';
import ClientPricingDialog from './ClientServicePricingDialog';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as accountingSlice from '../../../data/accounting.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import ClientPricingList from './ClientPricingList';
import { ClientServicePricing } from '../../../data/models/client-service-pricing.model';
import ConfirmationDialog from '../../ConfirmationDialog';
import ActivateDialog from '../ActivateDialog';
import ServicePricingConflictDialog from './ServicePricingConflictDialog';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'ClientPricing';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(3),
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
  },
}));

interface ClientPricingProps {
  clientBC: string;
  servicePlans: ServicePlan[];
  pricingModels: PricingModel[];
}

export default function ClientPricing({
  clientBC,
  servicePlans,
  pricingModels,
}: ClientPricingProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();

  const isSuperReaderUser = useIsSuperReaderUser();

  const clientServicePricings = useAppSelector(
    accountingSlice.selectClientServicePricings,
  );
  const loadingClientServicePricings = useAppSelector(
    accountingSlice.selectLoadingClientServicePricings,
  );
  const conflictingClientServicePricings = useAppSelector(
    accountingSlice.selectConflictingClientServicePricings,
  );

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [editedPricing, setEditedPricing] =
    React.useState<ClientServicePricing>();
  const [deletedPricing, setDeletedPricing] =
    React.useState<ClientServicePricing>();
  const [isDeactivate, setIsDeactivate] = React.useState(false);
  const [activateDialogTitle, setActivateDialogTitle] = React.useState('');
  const [activateDialogMessage, setActivateDialogMessage] = React.useState('');
  const [activateDialogPostiveFn, setActivateDialogPostiveFn] = React.useState<
    () => void
  >(() => null);
  const [activateDialogNegativeFn, setActivateDialogNegativeFn] =
    React.useState<() => void>(() => null);
  const [activateDialogOpen, setActivateDialogOpen] = React.useState(false);
  const [conflictDialogOpen, setConflictDialogOpen] = React.useState(false);

  const [activateUUID, setActivateUUID] = React.useState<string | null>(null);
  const [activateStart, setActivateStart] = React.useState<string>();
  const [activateEnd, setActivateEnd] = React.useState<string>();

  React.useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('chargeType'));
    dispatch(accountingSlice.fetchTransportationServicesAsync());
  }, []);

  React.useEffect(() => {
    if (clientBC) {
      dispatch(accountingSlice.fetchClientServicePricingsAsync(clientBC));
    }
  }, [clientBC]);

  const close = () => {
    setDialogOpen(false);
  };

  const onCreate = () => {
    setEditedPricing(undefined);
    setDialogOpen(true);
  };

  const onEdit = (evt: any, clientServicePricing: ClientServicePricing) => {
    evt.preventDefault();
    evt.stopPropagation();
    setEditedPricing(clientServicePricing);
    setDialogOpen(true);
  };

  const updatePricingEffectiveDates = (
    clientServicePricingUUID: string,
    start?: string,
    end?: string,
  ) => {
    dispatch(
      accountingSlice.updateClientServicePricingEffectiveDatesAsync(
        clientServicePricingUUID,
        start,
        end,
      ),
    );
  };

  React.useEffect(() => {
    if (conflictingClientServicePricings?.length === 0 && activateUUID) {
      // No conflicts so just update the dates
      updatePricingEffectiveDates(activateUUID, activateStart, activateEnd);
    } else if (
      conflictingClientServicePricings &&
      conflictingClientServicePricings.length > 0
    ) {
      setConflictDialogOpen(true);
    }
  }, [conflictingClientServicePricings]);

  const activatePricing = (evt: any, pricing: ClientServicePricing) => {
    evt.preventDefault();
    evt.stopPropagation();

    const confirm = (start?: string, end?: string) => {
      setActivateUUID(pricing.clientServicePricingUUID!);
      setActivateStart(start);
      setActivateEnd(end);
      const effectiveEnd = end === 'clear' ? undefined : end;
      dispatch(accountingSlice.setConflictingClientServicePricings(null));
      dispatch(
        accountingSlice.testClientServicePricingAsync({
          ...pricing,
          effectiveStartDate: start,
          effectiveEndDate: effectiveEnd,
        }),
      );
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(false);
    setActivateDialogTitle(t('client.confirmActivatePricingTitle'));
    setActivateDialogMessage(t('client.confirmActivatePricingMessage'));
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const deactivatePricing = (evt: any, pricing: ClientServicePricing) => {
    evt.preventDefault();
    evt.stopPropagation();

    const confirm = (start?: string, end?: string) => {
      updatePricingEffectiveDates(
        pricing.clientServicePricingUUID!,
        start,
        end,
      );
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(true);
    setActivateDialogTitle(t('client.confirmDeactivatePricingTitle'));
    setActivateDialogMessage(t('client.confirmDeactivatePricingMessage'));
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const onDelete = (evt: any, clientServicePricing: ClientServicePricing) => {
    evt.preventDefault();
    evt.stopPropagation();
    setDeletedPricing(clientServicePricing);
    setConfirmDialogOpen(true);
  };

  const confirmDelete = () => {
    if (deletedPricing) {
      dispatch(accountingSlice.deleteClientServicePricingAsync(deletedPricing));
    }
    setConfirmDialogOpen(false);
  };

  const cancelDelete = () => {
    setConfirmDialogOpen(false);
  };

  const confirmActivate = () => {
    updatePricingEffectiveDates(activateUUID!, activateStart, activateEnd);
    setConflictDialogOpen(false);
  };

  const cancelActivate = () => {
    setConflictDialogOpen(false);
  };

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.title}>
          {t('client.servicePricing')}
        </Typography>
        {!isSuperReaderUser && (
          <Button variant="contained" color="primary" onClick={onCreate}>
            {t('client.createServicePricing')}
          </Button>
        )}
      </Box>
      <ClientPricingList
        clientServicePricings={clientServicePricings}
        servicePlans={servicePlans}
        pricingModels={pricingModels}
        onEdit={onEdit}
        onActivate={activatePricing}
        onDeactivate={deactivatePricing}
        onDelete={onDelete}
      />
      <ClientPricingDialog
        clientServicePricing={editedPricing}
        open={dialogOpen}
        close={close}
        title={
          editedPricing
            ? t('client.updateServicePricing')
            : t('client.createServicePricing')
        }
        clientBC={clientBC}
        servicePlans={servicePlans}
        pricingModels={pricingModels}
      />
      <ConfirmationDialog
        title={t('client.confirmDeletePricingTitle')}
        message={t('client.confirmDeletePricingMessage')}
        open={confirmDialogOpen}
        positiveAction={confirmDelete}
        negativeAction={cancelDelete}
      />
      <ActivateDialog
        title={activateDialogTitle}
        message={activateDialogMessage}
        open={activateDialogOpen}
        forActivate={!isDeactivate}
        handleOk={activateDialogPostiveFn}
        handleCancel={activateDialogNegativeFn}
      />
      {conflictingClientServicePricings && (
        <ServicePricingConflictDialog
          open={conflictDialogOpen}
          conflicts={conflictingClientServicePricings}
          confirmActivate={confirmActivate}
          cancelActivate={cancelActivate}
        />
      )}
    </StyledBox>
  );
}
